import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import gridBlocks from "@/assets/images/landing-page/grid blocks.png";
import { MockupWindow, BoardMockup } from "@/pages/Public/components/ProductMockups";
import { trackCheckCtaClick } from "@/utils/analytics";
import { ROUTE_CONFIG } from "@/routes/config";

interface LandingIntroSectionProps {
	handleStart: (answer: "yes" | "no" | "unknown") => void;
}

export default function LandingIntroSection({ handleStart }: LandingIntroSectionProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string) => t(`questionnaire.landing.${key}`);

	const handleCheckStart = () => {
		trackCheckCtaClick("hero");
		handleStart("yes");
	};

	return (
		// Light ground, ink headline. `ink` is for headlines and the small dark surfaces
		// that already exist — badges, tooltips, section bands — not for a full-height
		// hero (guide §4.3). The grid pattern is a dark PNG, so it drops to a faint wash.
		<section className="relative w-full bg-surface py-12 sm:py-32 px-4 flex justify-center overflow-hidden">
			<div
				className="absolute inset-0 bg-no-repeat bg-cover bg-center z-0 opacity-[0.04]"
				style={{ backgroundImage: `url(${gridBlocks})` }}
			/>
			<div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-12 max-w-[1500px] w-full">
				<div className="flex flex-col gap-14 max-w-[694px] w-full">
					<div className="flex flex-col gap-6">
						<h1 className="font-urbanist font-bold text-ink text-[42px] md:text-[58px] leading-[120%]">
							{ts("title")}
						</h1>
						<p className="font-urbanist font-medium text-subtle text-[20px] md:text-[24px] leading-[140%]">
							{ts("subtitle")}
						</p>
					</div>
					<div className="flex flex-col sm:flex-row gap-4">
						{/* The one accented thing in this view: flat #FF5A24, bold, 16px, 6px
						    radius (guide §4.1). White on the accent is 3.12:1, so the weight
						    and size are load-bearing — not decoration. */}
						<button
							className="min-h-[52px] bg-accent hover:bg-accent-dark border border-transparent rounded-button px-[28px] py-[12px] text-[16px] font-bold font-urbanist text-white transition-colors"
							onClick={handleCheckStart}
						>
							{ts("ctaPrimary")}
						</button>
						{/* Ghost secondary: a bordered white button with an ink label, so the
						    accent stays the only thing marking the primary action (§4.2). */}
						<button
							className="min-h-[52px] bg-white border border-border rounded-button px-[28px] py-[12px] text-[16px] font-semibold font-urbanist text-ink hover:border-accent transition-colors"
							onClick={() => navigate(ROUTE_CONFIG.PRODUCT)}
						>
							{ts("ctaSecondary")}
						</button>
					</div>
					<p className="font-urbanist text-subtle text-[14px] -mt-8">{ts("ctaNote")}</p>
				</div>

				<div className="w-full max-w-[460px] md:max-w-[620px]">
					<MockupWindow>
						<BoardMockup />
					</MockupWindow>
				</div>
			</div>
		</section>
	);
}
